import { createInterfaceId, Implements, Service, ServiceLifetime } from '@gitlab/needle';
import { CliInitializationService } from '../../cli_initialization_service';
import { CredentialProvider } from '../../utils/credential_provider';

/**
 * Ensures the CLI backend is initialized. Unlike the TUI, missing credentials
 * are not fatal for ACP: the server starts unauthenticated and this is
 * re-invoked from the `authenticate` request once the user has logged in
 * (CredentialProvider re-probes glab on every miss).
 */
export interface AcpInitializationService {
  /**
   * Initializes on first call if credentials are available. Returns false
   * when credentials are missing, in which case `authenticate` should fail
   * with auth_required until this returns true.
   */
  ensureInitialized(): Promise<boolean>;
}

export const AcpInitializationService = createInterfaceId<AcpInitializationService>(
  'AcpInitializationService',
);

@Implements(AcpInitializationService)
@Service({
  dependencies: [CredentialProvider, CliInitializationService],
  lifetime: ServiceLifetime.Singleton,
})
export class DefaultAcpInitializationService implements AcpInitializationService {
  #credentialProvider: CredentialProvider;

  #initService: CliInitializationService;

  #initialized = false;

  #inflight: Promise<boolean> | undefined;

  constructor(credentialProvider: CredentialProvider, initService: CliInitializationService) {
    this.#credentialProvider = credentialProvider;
    this.#initService = initService;
  }

  async ensureInitialized(): Promise<boolean> {
    if (this.#initialized) return true;
    this.#inflight ??= (async () => {
      const { token } = await this.#credentialProvider.getCredentials();
      if (!token) return false;
      const { criticalError } = await this.#initService.initialize();
      if (criticalError) {
        throw new Error(criticalError);
      }
      this.#initialized = true;
      return true;
    })().finally(() => {
      this.#inflight = undefined;
    });
    return this.#inflight;
  }
}
